import { useEffect, useState, useRef } from "react";
import {
  Check, CheckCircle2, Clock, Hourglass, AlertTriangle, Ban, X as XIcon,
  Phone, MessageCircle, Instagram, Facebook, Globe, Star,
  UtensilsCrossed, CreditCard, UserCheck, Ticket, ArrowRight,
} from "lucide-react";
import { api, eventosPublicosApi, EventoPublicoCatalogo } from "../../lib/api";
import { FormPagoReserva } from "./FormPagoReserva";
import { aplicarFaviconPagina, aplicarTituloPagina } from "../../lib/branding";

/* Acento + logo por sucursal: vienen de reservar-info (logoUrl/colorPrimario
   de crm_menu_configuration de CADA sucursal). Este mismo bundle de frontend
   lo sirven los 4 tenants -- NUNCA hardcodear un color/logo por id numérico
   acá, porque el id=1 de un tenant no tiene nada que ver con el id=1 de otro. */
const PALETA = ["#3A6B8C", "#9A6A3C", "#5E7C53", "#8C4A52", "#6B5B95", "#A05A2C"];
function acentoDe(s: SucursalInfo): string {
  return s.colorPrimario ?? PALETA[(s.id - 1) % PALETA.length];
}

/** Extrae el @handle del último segmento de una URL de red social (Instagram/Facebook). */
function handleDeUrl(url?: string | null): string | null {
  if (!url) return null;
  try {
    const seg = new URL(url).pathname.split("/").filter(Boolean).pop();
    return seg ? `@${seg}` : null;
  } catch {
    return null;
  }
}

const SEGUNDOS_REDIRECCION = 8;

interface HorarioDia { inicio: string; fin: string; nextDay?: boolean; }
interface RecursoInfo { id: number; nombre: string; capacidad: number; horaInicio?: string | null; horaFin?: string | null; }
interface ZonaInfo {
  id: number; nombre: string; permiteSeleccionMesa?: boolean;
  bloquesTiempoMinutos?: number | null; recursos?: RecursoInfo[];
  horarioSemanal?: Record<string, HorarioDia | null> | null;
  // Pago opcional para reservar
  requierePagoReserva?: boolean;
  montoReservaUsd?: number | null;
  instruccionesPago?: string | null;
  mensajePoliticaReserva?: string | null;
}
interface SucursalInfo {
  id: number; codigo: string; nombre: string;
  logoUrl?: string | null; colorPrimario?: string | null;
}
interface SedeInfo {
  id: number; nombre: string; direccion?: string | null; telefono?: string | null;
  logoUrl?: string | null; colorPrimario?: string | null;
}
interface ReservarInfo {
  organizacionId: number;
  nombre: string;
  rubro: string | null;
  urlPostReserva: string | null;
  zonas: ZonaInfo[];
  sucursales: SucursalInfo[];
  sedesInformativas?: SedeInfo[];
  // Identidad del GRUPO (no de una sucursal puntual) -- se usa como favicon
  // mientras se muestra el selector de "elegí tu restaurante" (ninguna
  // sucursal elegida todavía).
  loginLogoUrl?: string | null;
  loginColorPrimario?: string | null;
}

function toMin(t: string): number {
  const [h, m] = t.split(':').map(Number);
  return h * 60 + m;
}

/** Genera slots de tiempo para la fecha y zona dadas, respetando horario semanal */
function generarSlotsParaDia(fecha: string, zona?: ZonaInfo | null): string[] {
  const slots: string[] = [];
  let iniMin = 7 * 60;  // 07:00 default
  let finMin = 23 * 60; // 23:00 default
  let nextDay = false;

  if (zona?.horarioSemanal && fecha) {
    const dia = new Date(fecha + 'T00:00:00').getDay();
    const cfg = zona.horarioSemanal[String(dia)];
    if (cfg === null || cfg === undefined) return []; // cerrado ese día
    if (cfg.inicio && cfg.fin) {
      iniMin = toMin(cfg.inicio);
      finMin = toMin(cfg.fin);
      nextDay = cfg.nextDay ?? false;
      if (nextDay) finMin += 1440;
    }
  }

  for (let m = iniMin; m < finMin; m += 30) {
    const realMin = m % 1440;
    const h = Math.floor(realMin / 60);
    const min = realMin % 60;
    slots.push(`${String(h).padStart(2, '0')}:${String(min).padStart(2, '0')}`);
  }
  return slots;
}

/** Slots genéricos sin zona (fallback) */
function generarSlots(): string[] {
  const slots: string[] = [];
  for (let h = 7; h <= 23; h++) {
    slots.push(`${String(h).padStart(2, "0")}:00`);
    slots.push(`${String(h).padStart(2, "0")}:30`);
  }
  return slots;
}
function hoyISO(): string {
  return new Date().toISOString().slice(0, 10);
}
function sucursalDeUrl(): number | undefined {
  const p = new URLSearchParams(window.location.search).get("sucursal");
  const n = Number(p);
  return p && !isNaN(n) && n > 0 ? n : undefined;
}

const STYLES = `
@import url('https://fonts.googleapis.com/css2?family=Fraunces:ital,opsz,wght@0,9..144,400;0,9..144,500;0,9..144,600;1,9..144,400&family=Jost:wght@300;400;500;600&display=swap');

.rsv-root {
  --paper:#F6F1E7; --paper-deep:#EFE6D5; --card:#FFFDF8;
  --ink:#2E2A24; --ink-soft:#736655; --line:#E4D9C5;
  --accent:#3A6B8C;
  min-height:100vh; position:relative; overflow-x:hidden;
  background:
    radial-gradient(1200px 600px at 15% -5%, color-mix(in srgb, var(--accent) 12%, transparent), transparent 60%),
    radial-gradient(900px 500px at 110% 10%, rgba(154,106,60,0.10), transparent 55%),
    var(--paper);
  font-family:'Jost', sans-serif; color:var(--ink);
  padding:0 18px 56px;
}
.rsv-grain {
  position:fixed; inset:0; pointer-events:none; z-index:0; opacity:0.05;
  background-image:url("data:image/svg+xml,%3Csvg viewBox='0 0 220 220' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='3'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E");
}
.rsv-wrap { position:relative; z-index:1; max-width:560px; margin:0 auto; }

.rsv-serif { font-family:'Fraunces', Georgia, serif; }

.rsv-head { text-align:center; padding:46px 0 26px; }
.rsv-kicker {
  font-size:12px; letter-spacing:.34em; text-transform:uppercase;
  color:var(--ink-soft); font-weight:500; margin:0 0 12px;
}
.rsv-title {
  font-family:'Fraunces', Georgia, serif; font-weight:400; font-size:46px;
  line-height:1.02; margin:0; color:var(--ink); letter-spacing:-0.01em;
}
.rsv-title em { font-style:italic; color:var(--accent); }
.rsv-rule { width:46px; height:2px; background:var(--accent); border-radius:2px; margin:18px auto 0; opacity:.7; }

/* Promoción de eventos públicos */
.rsv-eventos-banner { display:flex; flex-wrap:wrap; justify-content:center; gap:8px; margin:18px 0 6px; }
.rsv-evento-chip {
  display:inline-flex; align-items:center; gap:6px; padding:8px 14px; border-radius:999px;
  background:rgba(255,255,255,0.85); border:1px solid var(--accent); color:var(--accent);
  font-size:12.5px; font-weight:600; text-decoration:none; transition:transform .15s ease, box-shadow .15s ease;
  box-shadow:0 2px 8px rgba(0,0,0,0.06);
}
.rsv-evento-chip:hover { transform:translateY(-1px); box-shadow:0 4px 14px rgba(0,0,0,0.12); }

/* Selector de sucursal */
.rsv-step-label {
  font-size:12px; letter-spacing:.28em; text-transform:uppercase;
  color:var(--ink-soft); font-weight:500; margin:0 0 14px; text-align:center;
}
.rsv-cards { display:grid; grid-template-columns:1fr; gap:14px; margin-bottom:30px; }
@media(min-width:560px){ .rsv-cards { grid-template-columns:1fr 1fr; } }

.rsv-card {
  position:relative; text-align:left; cursor:pointer; background:var(--card);
  border:1.5px solid var(--line); border-radius:18px; padding:22px 20px;
  transition:transform .25s cubic-bezier(.2,.8,.2,1), box-shadow .25s, border-color .25s;
  box-shadow:0 2px 10px rgba(46,42,36,0.04); font-family:inherit;
}
.rsv-card:hover { transform:translateY(-3px); box-shadow:0 14px 34px rgba(46,42,36,0.12); }
.rsv-card.is-sel { border-color:var(--c-acc); box-shadow:0 14px 34px color-mix(in srgb, var(--c-acc) 22%, transparent); }
.rsv-card.is-sel::after {
  content:"✓"; position:absolute; top:14px; right:14px; width:26px; height:26px;
  border-radius:50%; background:var(--c-acc); color:#fff; font-size:14px;
  display:flex; align-items:center; justify-content:center;
}
.rsv-mono {
  width:46px; height:46px; border-radius:50%; display:flex; align-items:center; justify-content:center;
  font-family:'Fraunces', serif; font-size:22px; color:#fff; background:var(--c-acc); margin-bottom:14px;
}
.rsv-card-logo { height:34px; width:auto; max-width:80%; display:block; margin-bottom:14px; }
.rsv-card-name { font-family:'Fraunces', serif; font-size:23px; font-weight:500; margin:0; color:var(--ink); }
.rsv-card-tag { font-size:13px; color:var(--c-acc); font-style:italic; margin:2px 0 10px; font-family:'Fraunces', serif; }
.rsv-card-addr { font-size:12.5px; color:var(--ink-soft); line-height:1.5; margin:0; }

/* Formulario */
.rsv-form {
  background:var(--card); border:1px solid var(--line); border-radius:22px;
  padding:30px 24px; box-shadow:0 18px 50px rgba(46,42,36,0.08);
}
.rsv-form-head { text-align:center; margin-bottom:24px; }
.rsv-form-head h2 { font-family:'Fraunces', serif; font-weight:500; font-size:25px; margin:0; color:var(--ink); }
.rsv-form-head p { font-size:13px; color:var(--ink-soft); margin:4px 0 0; letter-spacing:.04em; }

.rsv-field { margin-bottom:18px; }
.rsv-row { display:flex; gap:12px; }
.rsv-row > * { flex:1; }
.rsv-label {
  display:block; font-size:12px; letter-spacing:.06em; color:var(--ink-soft);
  margin-bottom:7px; font-weight:500; text-transform:uppercase;
}
.rsv-input, .rsv-select, .rsv-textarea {
  width:100%; box-sizing:border-box; padding:12px 14px; border-radius:11px;
  border:1.5px solid var(--line); background:#FCFAF4; color:var(--ink);
  font-size:16px; font-family:'Jost', sans-serif; outline:none;
  transition:border-color .2s, box-shadow .2s, background .2s;
}
.rsv-input::placeholder, .rsv-textarea::placeholder { color:#8B7D66; }
.rsv-input:focus, .rsv-select:focus, .rsv-textarea:focus {
  border-color:var(--accent); background:#fff;
  outline: 2px solid color-mix(in srgb, var(--accent) 35%, transparent);
  outline-offset: 1px;
  box-shadow:0 0 0 3px color-mix(in srgb, var(--accent) 18%, transparent);
}
.rsv-textarea { min-height:74px; resize:vertical; }

.rsv-stepper { display:flex; align-items:center; gap:14px; }
.rsv-step-btn {
  width:46px; height:46px; border-radius:12px; border:1.5px solid var(--line);
  background:#FCFAF4; color:var(--accent); font-size:24px; cursor:pointer; line-height:1;
  display:flex; align-items:center; justify-content:center; transition:background .2s, border-color .2s;
  font-family:'Jost', sans-serif;
}
.rsv-step-btn:hover { background:#fff; border-color:var(--accent); }
.rsv-step-val { font-family:'Fraunces', serif; font-size:26px; min-width:46px; text-align:center; color:var(--ink); }

.rsv-chips { display:flex; flex-wrap:wrap; gap:9px; }
.rsv-chip {
  padding:9px 16px; border-radius:30px; font-size:14px; cursor:pointer; font-family:'Jost', sans-serif;
  border:1.5px solid var(--line); background:#fff; color:var(--ink); transition:all .2s;
}
.rsv-chip:hover { border-color:var(--accent); }
.rsv-chip.is-sel { background:var(--accent); border-color:var(--accent); color:#fff; }

.rsv-error {
  background:#FBEAEA; border:1px solid #ECC4C4; color:#9C3838;
  padding:11px 14px; border-radius:11px; font-size:14px; margin-bottom:18px;
}

.rsv-submit {
  width:100%; padding:16px; border-radius:13px; border:none; cursor:pointer;
  background:var(--accent); color:#fff; font-size:16px; font-weight:500; letter-spacing:.04em;
  font-family:'Jost', sans-serif; margin-top:6px; transition:filter .2s, transform .1s, opacity .2s;
}
.rsv-submit:hover:not(:disabled) { filter:brightness(.93); }
.rsv-submit:active:not(:disabled) { transform:translateY(1px); }
.rsv-submit:disabled { opacity:.5; cursor:not-allowed; }

.rsv-foot { text-align:center; color:var(--ink-soft); font-size:12px; margin-top:24px; letter-spacing:.06em; }

/* Éxito */
.rsv-ok-wrap { display:flex; align-items:center; justify-content:center; min-height:100vh; }
.rsv-ok {
  background:var(--card); border:1px solid var(--line); border-radius:24px;
  padding:56px 40px; max-width:500px; width:100%; text-align:center;
  box-shadow:0 22px 60px rgba(46,42,36,0.12);
}
.rsv-ok-mark {
  width:72px; height:72px; border-radius:50%; background:var(--accent); color:#fff;
  display:flex; align-items:center; justify-content:center; margin:0 auto 22px;
  box-shadow:0 12px 28px color-mix(in srgb, var(--accent) 32%, transparent);
}
.rsv-ok-mark > svg { display:block; }
.rsv-ok-wa {
  display: inline-flex !important; align-items: center; justify-content: center; gap: 8px;
  background: #25D366 !important; text-decoration: none !important;
}
.rsv-social {
  text-align: center; padding: 24px 0 28px;
  display: flex; justify-content: center; gap: 18px; flex-wrap: wrap;
}
.rsv-social-item {
  display: flex; flex-direction: column; align-items: center; gap: 6px;
}
.rsv-social-link {
  display: inline-flex; align-items: center; justify-content: center;
  width: 48px; height: 48px; border-radius: 50%;
  background: var(--paper-deep); color: var(--ink-soft);
  text-decoration: none; transition: background .18s, color .18s, transform .12s;
}
.rsv-social-link:hover {
  background: var(--accent); color: #fff; transform: translateY(-1px);
}
.rsv-social-handle {
  font-size: 12px; color: var(--ink-soft); font-family: 'Jost', sans-serif;
  max-width: 100px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
.rsv-otras-sucursales {
  margin-top: 28px; padding-top: 24px; border-top: 1px solid var(--line);
}
.rsv-otras-sucursales-titulo {
  font-size: 13px; color: var(--ink-soft); margin: 0 0 14px; font-family: 'Jost', sans-serif;
}
.rsv-otras-sucursales-lista {
  display: flex; justify-content: center; gap: 16px; flex-wrap: wrap;
}
.rsv-otra-sucursal {
  display: flex; flex-direction: column; align-items: center; gap: 6px;
  text-decoration: none; color: var(--ink-soft); font-size: 12px; font-family: 'Jost', sans-serif;
  transition: transform .15s ease;
}
.rsv-otra-sucursal:hover { transform: translateY(-2px); color: var(--ink); }
.rsv-sedes-info { display:flex; flex-direction:column; gap:8px; margin-top:12px; }
.rsv-sede-info { display:flex; align-items:center; gap:12px; text-decoration:none; padding:10px 12px; border:1px solid color-mix(in srgb, var(--accent) 18%, transparent); border-radius:12px; background:#fff; transition:transform .15s, border-color .15s; }
.rsv-sede-info:hover { transform:translateY(-1px); border-color:var(--accent); }
.rsv-sede-info img, .rsv-sede-info .rsv-otra-sucursal-mono { width:40px; height:40px; border-radius:50%; object-fit:cover; flex-shrink:0; }
.rsv-sede-info-txt { display:flex; flex-direction:column; min-width:0; text-align:left; }
.rsv-sede-info-txt b { color:var(--ink); font-size:14px; }
.rsv-sede-info-txt small { color:var(--ink-soft); font-size:12px; line-height:1.3; }
.rsv-sede-info-txt em { color:var(--accent); font-size:12px; font-style:normal; font-weight:600; margin-top:2px; }
.rsv-otra-sucursal img, .rsv-otra-sucursal-mono {
  width: 52px; height: 52px; border-radius: 50%; object-fit: cover;
  border: 2px solid var(--line); box-shadow: 0 4px 12px rgba(0,0,0,0.08);
}
.rsv-otra-sucursal-mono {
  display: flex; align-items: center; justify-content: center;
  background: var(--paper-deep); color: var(--ink-soft); font-weight: 600; font-size: 18px;
}

/* Radio group accesible para "Sexo" — input nativo oculto visualmente */
.rsv-fieldset { border: none; margin: 0; padding: 0; }
.rsv-fieldset > legend { padding: 0; }
.rsv-radios { display: flex; gap: 8px; flex-wrap: wrap; }
.rsv-radio {
  flex: 1 1 0; min-width: 0;
  display: inline-flex; align-items: center; justify-content: center;
  padding: 10px 14px; border-radius: 12px; cursor: pointer;
  border: 1.5px solid var(--line); background: transparent;
  color: var(--ink-soft); font-family: 'Jost', sans-serif; font-size: 14px;
  transition: border-color .18s, background .18s, color .18s, transform .12s;
  min-height: 44px;
}
.rsv-radio input { position: absolute; opacity: 0; pointer-events: none; width: 0; height: 0; }
.rsv-radio:hover:not(.is-sel) { border-color: var(--accent); color: var(--ink); }
.rsv-radio.is-sel {
  border: 2px solid var(--accent); background: var(--accent); color: #fff;
}
.rsv-radio.is-sug:not(.is-sel) {
  border: 1.5px dashed #c9b896;
}
.rsv-radio:has(input:focus-visible) {
  outline: 2px solid color-mix(in srgb, var(--accent) 50%, transparent);
  outline-offset: 2px;
}
.rsv-radio-apply {
  padding: 10px 14px; border-radius: 12px; cursor: pointer; min-height: 44px;
  border: 1.5px solid var(--accent); color: var(--accent); background: transparent;
  font-family: 'Jost', sans-serif; font-size: 13px;
  transition: background .18s, color .18s;
}
.rsv-radio-apply:hover { background: var(--accent); color: #fff; }

/* Hover lift + halo en botones primarios del success screen */
.rsv-ok-link {
  transition: transform .15s ease, box-shadow .15s ease, filter .15s;
  display: inline-flex; align-items: center; justify-content: center; gap: 8px;
  text-decoration: none;
}
.rsv-ok-link:hover {
  transform: translateY(-2px);
  box-shadow: 0 12px 24px color-mix(in srgb, var(--accent) 28%, transparent);
}
.rsv-ok-link:active { transform: translateY(0); }
.rsv-ok-again {
  transition: background .15s, color .15s, transform .12s;
}
.rsv-ok-again:hover { transform: translateY(-1px); }

/* Chip wrap defensivo en pantallas muy estrechas (<360px) */
@media (max-width: 360px) {
  .rsv-chips {
    flex-wrap: wrap;
  }
  .rsv-chip { flex: 1 1 calc(50% - 6px); }
}
.rsv-ok h1 { font-family:'Fraunces', serif; font-weight:400; font-size:30px; margin:0 0 10px; color:var(--ink); }
.rsv-ok p { font-size:15.5px; color:var(--ink-soft); line-height:1.65; margin:0 0 26px; }
.rsv-ok b { color:var(--accent); font-family:'Fraunces', serif; }
.rsv-ok-link {
  width:100%; padding:14px; border-radius:12px; border:none; background:var(--accent);
  color:#fff; font-size:15px; cursor:pointer; font-family:'Jost', sans-serif; margin-bottom:14px;
}
.rsv-ok-again {
  background:transparent; border:1.5px solid var(--line); color:var(--ink-soft);
  padding:11px 26px; border-radius:11px; font-size:14px; cursor:pointer; font-family:'Jost', sans-serif;
  margin-top:14px;
}
.rsv-ok-small { font-size:12.5px; color:var(--ink-soft); margin:0; }

/* Modal de error de disponibilidad */
.rsv-modal-overlay {
  position:fixed; inset:0; background:rgba(46,42,36,0.55); backdrop-filter:blur(4px);
  z-index:100; display:flex; align-items:center; justify-content:center; padding:20px;
  animation:rsvUp .25s ease both;
}
.rsv-modal {
  background:var(--card); border-radius:22px; max-width:400px; width:100%;
  box-shadow:0 24px 60px rgba(46,42,36,0.22); overflow:hidden;
}
.rsv-modal-head {
  padding:22px 24px 16px; border-bottom:1.5px solid var(--line);
  display:flex; align-items:center; gap:12px;
}
.rsv-modal-icon {
  width:44px; height:44px; border-radius:50%; background:#FBEAEA;
  display:flex; align-items:center; justify-content:center; font-size:22px; shrink:0;
}
.rsv-modal-head h3 { font-family:'Fraunces',serif; font-size:20px; color:var(--ink); margin:0; }
.rsv-modal-body { padding:16px 24px 8px; }
.rsv-modal-body p { font-size:15px; color:var(--ink-soft); line-height:1.6; margin:0; white-space:pre-line; }
.rsv-modal-actions { padding:16px 24px 22px; display:flex; flex-direction:column; gap:10px; }
.rsv-modal-btn-primary {
  width:100%; padding:14px; border-radius:12px; border:none; cursor:pointer;
  background:var(--accent); color:#fff; font-size:15px; font-weight:500;
  font-family:'Jost',sans-serif; transition:filter .15s;
}
.rsv-modal-btn-primary:hover { filter:brightness(.92); }
.rsv-modal-btn-secondary {
  width:100%; padding:12px; border-radius:12px; background:transparent;
  border:1.5px solid var(--line); color:var(--ink-soft); font-size:14px;
  cursor:pointer; font-family:'Jost',sans-serif;
}
.rsv-modal-btn-secondary:hover { background:var(--paper-deep); }

@keyframes rsvUp { from { opacity:0; transform:translateY(14px); } to { opacity:1; transform:none; } }
.rsv-reveal { animation:rsvUp .6s cubic-bezier(.2,.8,.2,1) both; }
`;

interface SucConfig {
  telefono?: string;
  whatsappNumero?: string;
  instagramUrl?: string;
  facebookUrl?: string;
  tripadvisorUrl?: string;
  websiteUrl?: string;
  politicaConfirmacionHoras: number;
  usaDuracionReserva: boolean;
  duracionDefaultMinutos: number;
  duracionOpciones: number[];
  colorPrimario?: string;
  logoUrl?: string | null;
}

export default function PublicReservar() {
  const [info, setInfo] = useState<ReservarInfo | null>(null);
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errorModal, setErrorModal] = useState<{ titulo: string; mensaje: string; esBloqueoPista: boolean } | null>(null);
  const [exito, setExito] = useState<{ id: number; estado?: string; esperaPosicion?: number | null } | null>(null);
  const [pagoReportado, setPagoReportado] = useState(false);
  const [contador, setContador] = useState(SEGUNDOS_REDIRECCION);
  const [sucConfig, setSucConfig] = useState<SucConfig | null>(null);
  // Config visual: logo, fondo, colores
  const [menuConfig, setMenuConfig] = useState<{ logoUrl?: string | null; imagenFondoUrl?: string | null; colorPrimario?: string } | null>(null);
  // Eventos públicos activos -- para promocionarlos en este link público
  const [eventosDestacados, setEventosDestacados] = useState<EventoPublicoCatalogo[]>([]);

  const [form, setForm] = useState({
    sucursalId: sucursalDeUrl(),
    nombreCliente: "",
    telefono: "",
    email: "",
    cif: "",
    fechaNacimiento: "",
    sexo: "" as "" | "M" | "F",
    fechaReserva: hoyISO(),
    horaReserva: "20:00",
    numPersonas: 2,
    zonaId: undefined as number | undefined,
    recursoId: undefined as number | undefined,
    requerimientosEspeciales: "",
    duracionMinutos: undefined as number | undefined,
    instagram: "",
    twitter: "",
    facebook: "",
    tiktok: "",
  });
  const [sexoSugerido, setSexoSugerido] = useState<"M" | "F" | null>(null);
  const [mostrarRedes, setMostrarRedes] = useState(false);

  // Promoción de eventos públicos activos en este link de reservas
  useEffect(() => {
    eventosPublicosApi.catalogo().then((evs) => setEventosDestacados(evs.slice(0, 3))).catch(() => setEventosDestacados([]));
  }, []);

  // ── Lookup automático de cliente existente por teléfono o cédula ──
  interface ClienteLookup { nombreCliente: string; email?: string; telefono?: string; mobil?: string; cif?: string; fechaNacimiento?: string; sexo?: string; }
  const [clienteAutocompletado, setClienteAutocompletado] = useState<string | null>(null);
  const lookupTimer = useRef<ReturnType<typeof setTimeout>>();
  const lastLookupKey = useRef("");

  useEffect(() => {
    clearTimeout(lookupTimer.current);
    lookupTimer.current = setTimeout(() => {
      const telClean = form.telefono.replace(/[\s\-()]/g, "");
      const cifClean = form.cif.trim().toUpperCase();
      const key = `${telClean}|${cifClean}`;
      if (key === lastLookupKey.current) return;
      lastLookupKey.current = key;

      const tieneTel = telClean.length >= 7;
      const tieneCif = cifClean.length >= 5;
      if (!tieneTel && !tieneCif) { setClienteAutocompletado(null); return; }

      const params = new URLSearchParams();
      if (tieneTel) params.set("telefono", telClean);
      if (tieneCif) params.set("cif", cifClean);

      api<ClienteLookup | null>(`/v1/reservas/lookup-cliente?${params}`)
        .then((r) => {
          if (!r) { setClienteAutocompletado(null); return; }
          setForm(prev => {
            const next = {
              ...prev,
              nombreCliente: r.nombreCliente || prev.nombreCliente,
              telefono: r.telefono || r.mobil || prev.telefono,
              cif: r.cif || prev.cif,
              email: r.email || prev.email,
              fechaNacimiento: r.fechaNacimiento || prev.fechaNacimiento,
              sexo: (r.sexo as "" | "M" | "F") || prev.sexo,
            };
            // Actualizamos la key ya mismo para que el autocompletado de
            // telefono/cif no dispare una segunda búsqueda redundante
            // (ambos son dependencias de este mismo effect).
            lastLookupKey.current = `${next.telefono.replace(/[\s\-()]/g, "")}|${next.cif.trim().toUpperCase()}`;
            return next;
          });
          setClienteAutocompletado(r.nombreCliente);
          setTimeout(() => setClienteAutocompletado(null), 4000);
        })
        .catch(() => {});
    }, 600);
    return () => clearTimeout(lookupTimer.current);
  }, [form.telefono, form.cif]);

  // Sugerir sexo por el primer nombre (lookup ligero + heuristica por terminación)
  useEffect(() => {
    if (form.sexo) { setSexoSugerido(null); return; }
    const nombre = form.nombreCliente.trim();
    if (nombre.length < 3) { setSexoSugerido(null); return; }
    const primero = nombre.normalize("NFD").replace(/[̀-ͯ]/g, "").toUpperCase().split(/\s+/)[0];
    const FEM = new Set(["MARIA","ANA","CARMEN","ROSA","ISABEL","LAURA","SOFIA","VALERIA",
      "VICTORIA","ANDREA","ALEJANDRA","JENNIFER","DIANA","CAROLINA","BEATRIZ","GABRIELA",
      "DANIELA","ADRIANA","PATRICIA","FERNANDA","CLAUDIA","JESSICA","NATALIA","STEFANIA",
      "VANESSA","MELISSA","KAREN","LORENA","CRISTINA","SUSANA","LUISA","ALICIA","BARBARA",
      "ELENA","SILVIA","MICHELLE","NICOLE","MARGARITA","LUCIA","ROSARIO","TERESA","JULIA",
      "MARCELA","MONICA","VERONICA","XIMENA","PAULA","CAMILA","SARA","SAMANTHA","CAROL"]);
    const MAS = new Set(["JOSE","JUAN","CARLOS","LUIS","JESUS","MIGUEL","FRANCISCO","PEDRO",
      "ANTONIO","RAFAEL","DAVID","JORGE","DIEGO","DANIEL","GABRIEL","FERNANDO","ALBERTO",
      "RICARDO","ROBERTO","EDUARDO","RAUL","MARIO","JAVIER","GUSTAVO","OSCAR","PABLO",
      "ANDRES","VICTOR","OMAR","ENRIQUE","CESAR","ARMANDO","HECTOR","RAMON","FELIX","RUBEN",
      "ALEXIS","ADRIAN","BENJAMIN","CRISTIAN","DIEGO","EDGAR","EMILIO","FABIAN","FELIPE",
      "GERARDO","GUILLERMO","HENRY","HUGO","IGNACIO","IVAN","JAIME","JONATHAN","JULIAN",
      "JULIO","LEONARDO","MANUEL","MARCO","MARTIN","MAURICIO","NICOLAS","ORLANDO","PATRICIO",
      "RAMIRO","RICARDO","RODRIGO","SAMUEL","SANTIAGO","SEBASTIAN","SERGIO","SIMON","TOMAS",
      "WILSON"]);
    if (FEM.has(primero)) { setSexoSugerido("F"); return; }
    if (MAS.has(primero)) { setSexoSugerido("M"); return; }
    // Heuristica simple por terminacion
    if (primero.endsWith("A") && !primero.endsWith("UA")) { setSexoSugerido("F"); return; }
    if (primero.endsWith("O")) { setSexoSugerido("M"); return; }
    setSexoSugerido(null);
  }, [form.nombreCliente, form.sexo]);

  // Zona seleccionada con sus recursos
  const zonaSelObj = info?.zonas.find(z => z.id === form.zonaId);

  const sucursales = info?.sucursales ?? [];
  const suc = sucursales.find((s) => s.id === form.sucursalId);
  // Usar el color del branding si está disponible, sino el color por defecto de la sucursal
  const accent = menuConfig?.colorPrimario ?? (suc ? acentoDe(suc) : "#6E6354");

  // Favicon: si ya hay una sucursal específica (auto-seleccionada, elegida,
  // o vía ?sucursal=N) usa SU logo; si todavía se está mostrando el selector
  // de "elegí tu restaurante" (varias sucursales, ninguna elegida), usa el
  // logo del GRUPO si está seteado.
  useEffect(() => {
    aplicarFaviconPagina(menuConfig?.logoUrl ?? info?.loginLogoUrl ?? null);
    aplicarTituloPagina(suc?.nombre ?? info?.nombre ?? null);
  }, [menuConfig?.logoUrl, info?.loginLogoUrl, suc?.nombre, info?.nombre]);

  useEffect(() => {
    api<ReservarInfo>("/v1/reservas/reservar-info")
      .then((r) => {
        setInfo(r);
        // Auto-seleccionar sucursal si hay una sola
        const autoSucId = r.sucursales.length === 1 ? r.sucursales[0].id : undefined;
        // Preferir zona con permite_seleccion_mesa=true (para mostrar mesas/pistas directamente)
        const zonaPref = r.zonas.find(z => z.permiteSeleccionMesa && (z.recursos ?? []).length > 0)
          ?? (r.zonas.length > 0 ? r.zonas[0] : undefined);
        // Sucursal efectiva: auto-select (1 sola) o la de la URL (?sucursal=N)
        const sucEfectiva = autoSucId ?? sucursalDeUrl();
        setForm((f) => ({
          ...f,
          sucursalId: sucEfectiva ?? f.sucursalId,
          zonaId: zonaPref?.id ?? f.zonaId,
        }));
        // Cargar config (logo, fondo, color, duración) de la sucursal efectiva
        if (sucEfectiva) {
          fetch(`/api/v1/sucursales/${sucEfectiva}/config`)
            .then(res => res.json())
            .then(setSucConfig)
            .catch(() => {});
          fetch(`/api/v1/menu-publico/sucursal/${sucEfectiva}/config`)
            .then(res => res.json())
            .then(d => setMenuConfig({ logoUrl: d.logoUrl, imagenFondoUrl: d.imagenFondoUrl, colorPrimario: d.colorPrimario }))
            .catch(() => {});
        }
      })
      .catch(() => setInfo({ organizacionId: 1, nombre: "", rubro: null, urlPostReserva: null, zonas: [], sucursales: [] }));
  }, []);

  useEffect(() => {
    if (!exito || !info?.urlPostReserva) return;
    if (contador <= 0) { window.location.href = info.urlPostReserva; return; }
    const t = setTimeout(() => setContador((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [exito, contador, info]);

  // Cierra el modal de error con Escape (accesibilidad WCAG 2.1.2)
  useEffect(() => {
    if (!errorModal) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setErrorModal(null); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [errorModal]);

  // Scroll a la zona de error en validación (mejora UX en forms largos)
  useEffect(() => {
    if (!error) return;
    const el = document.querySelector(".rsv-error");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [error]);

  function set<K extends keyof typeof form>(k: K, v: (typeof form)[K]) {
    setForm((f) => ({ ...f, [k]: v }));
  }

  function elegir(id: number) {
    set("sucursalId", id);
    setError(null);
    // Cargar config de la sucursal seleccionada
    fetch(`/api/v1/sucursales/${id}/config`)
      .then(r => r.json())
      .then(d => setSucConfig(d))
      .catch(() => {});
    // Cargar logo, fondo y color
    fetch(`/api/v1/menu-publico/sucursal/${id}/config`)
      .then(r => r.json())
      .then(d => setMenuConfig({ logoUrl: d.logoUrl, imagenFondoUrl: d.imagenFondoUrl, colorPrimario: d.colorPrimario }))
      .catch(() => {});
    setTimeout(() => {
      document.getElementById("rsv-form")?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 60);
  }

  async function enviar() {
    setError(null);
    if (!form.sucursalId) { setError("Elegí en qué restaurante querés reservar"); return; }
    if (!form.nombreCliente.trim()) { setError("Por favor ingresá tu nombre"); return; }
    if (!form.telefono.trim() && !form.email.trim()) {
      setError("Dejanos un teléfono o email para confirmarte"); return;
    }
    const hoy = new Date(); hoy.setHours(0, 0, 0, 0);
    const f = new Date(form.fechaReserva + "T00:00:00");
    if (f < hoy) { setError("La fecha no puede ser pasada"); return; }
    if (form.numPersonas < 1 || form.numPersonas > 30) {
      setError("El número de personas debe estar entre 1 y 30"); return;
    }

    setEnviando(true);
    try {
      const res = await api<{ id: number; estado: string; esperaPosicion?: number | null }>("/v1/reservas/public", {
        method: "POST",
        body: {
          sucursalId: form.sucursalId,
          nombreCliente: form.nombreCliente.trim(),
          telefono: form.telefono.trim() || undefined,
          email: form.email.trim() || undefined,
          cif: form.cif.trim() || undefined,
          fechaNacimiento: form.fechaNacimiento || undefined,
          sexo: form.sexo || (sexoSugerido ?? undefined),
          fechaReserva: form.fechaReserva,
          horaReserva: form.horaReserva,
          numPersonas: form.numPersonas,
          zonaId: form.zonaId,
          recursoId: form.recursoId,
          requerimientosEspeciales: form.requerimientosEspeciales.trim() || undefined,
          duracionMinutos: form.duracionMinutos,
          instagram: form.instagram.trim() || undefined,
          twitter: form.twitter.trim() || undefined,
          facebook: form.facebook.trim() || undefined,
          tiktok: form.tiktok.trim() || undefined,
          origen: "web_publica",
        },
      });
      setExito({ id: res.id, estado: res.estado, esperaPosicion: res.esperaPosicion });
      setContador(SEGUNDOS_REDIRECCION);
    } catch (e: any) {
      const msg: string = e.message ?? "No pudimos procesar tu reserva. Intentá de nuevo.";
      // Errores de bloqueo o pista ocupada → popup visible
      const esBloqueoPista = msg.toLowerCase().includes('bloqueada') || msg.toLowerCase().includes('bloqueado') || msg.toLowerCase().includes('reservada');
      if (esBloqueoPista) {
        const nombrePista = zonaSelObj?.permiteSeleccionMesa && form.recursoId
          ? (zonaSelObj.recursos?.find(r => r.id === form.recursoId)?.nombre ?? "esta pista")
          : "esta opción";
        setErrorModal({
          titulo: "Pista / Mesa no disponible",
          mensaje: `La ${nombrePista} está bloqueada o ya tiene una reserva en el horario elegido.\n\nPuedes intentar con otra pista disponible o seleccionar un horario diferente.`,
          esBloqueoPista: true,
        });
      } else {
        setError(msg);
      }
    } finally {
      setEnviando(false);
    }
  }

  // ───────── Pantalla de éxito ─────────
  if (exito) {
    const url = info?.urlPostReserva;
    // ¿La zona elegida requiere pago para confirmar?
    const requierePago = !!zonaSelObj?.requierePagoReserva
      && exito.estado !== "lista_espera"
      && !pagoReportado;

    // Formulario de pago — aparece sólo si la zona lo requiere y aún no reportó
    if (requierePago) {
      return (
        <div className="rsv-root" style={{
          ["--accent" as any]: accent,
          ...(menuConfig?.imagenFondoUrl ? {
            backgroundImage: `linear-gradient(rgba(246,241,231,0.88), rgba(246,241,231,0.88)), url(${menuConfig.imagenFondoUrl})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            backgroundAttachment: "fixed",
          } : {}),
        }}>
          <style>{STYLES}</style>
          <div className="rsv-grain" />
          <div className="rsv-ok-wrap" style={{ flexDirection: 'column', padding: '24px 16px' }}>
            <div className="rsv-ok rsv-reveal" style={{ marginBottom: 16, padding: '30px 24px' }}>
              <div className="rsv-ok-mark" aria-hidden>
                <Check size={32} strokeWidth={2.6} />
              </div>
              <h1 style={{ fontSize: 20 }}>Reserva #{exito.id} creada</h1>
              <p>
                Para confirmar tu lugar en <b>{zonaSelObj?.nombre}</b>{" "}
                {suc ? <>de <b>{suc.nombre}</b></> : null} necesitamos que reportes tu pago.
              </p>
              <p style={{ fontSize: 13, color: 'var(--ink-soft)', marginTop: 4 }}>
                Guardá este enlace: <a href={`/reserva/${exito.id}`}
                  style={{ color: 'var(--accent)', textDecoration: 'underline', fontWeight: 600 }}>
                  /reserva/{exito.id}
                </a>
              </p>
            </div>
            <FormPagoReserva
              reservaId={exito.id}
              montoSugerido={zonaSelObj?.montoReservaUsd ?? null}
              instrucciones={zonaSelObj?.instruccionesPago ?? null}
              defaults={{
                nombre: form.nombreCliente,
                telefono: form.telefono,
                email: form.email,
                cif: form.cif,
              }}
              onReportado={() => setPagoReportado(true)}
            />
          </div>
        </div>
      );
    }
    return (
      <div className="rsv-root" style={{
        ["--accent" as any]: accent,
        ...(menuConfig?.imagenFondoUrl ? {
          backgroundImage: `linear-gradient(rgba(246,241,231,0.88), rgba(246,241,231,0.88)), url(${menuConfig.imagenFondoUrl})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundAttachment: "fixed",
        } : {}),
      }}>
        <style>{STYLES}</style>
        <div className="rsv-grain" />
        <div className="rsv-ok-wrap">
          <div className="rsv-ok rsv-reveal">
            <div className="rsv-ok-mark" aria-hidden>
              {pagoReportado
                ? <CreditCard size={30} strokeWidth={2.2} />
                : exito.estado === "lista_espera"
                  ? <Hourglass size={30} strokeWidth={2.2} />
                  : <Check size={32} strokeWidth={2.6} />}
            </div>
            <h1>
              {pagoReportado
                ? "¡Pago recibido!"
                : exito.estado === "lista_espera"
                  ? "Estás en lista de espera"
                  : "¡Gracias por tu reserva!"}
            </h1>
            {pagoReportado && (
              <p>
                Recibimos tu reporte de pago para la reserva <b>#{exito.id}</b>.<br />
                Vamos a verificar el pago y te confirmamos por WhatsApp o email en breve.
              </p>
            )}
            {!pagoReportado && (
              <>
                {exito.estado === "lista_espera" ? (
                  <p>
                    Tu solicitud <b>#{exito.id}</b>
                    {suc ? <> en <b>{suc.nombre}</b></> : null} entró en{" "}
                    <b>lista de espera</b>{" "}
                    {exito.esperaPosicion ? <>(posición <b>#{exito.esperaPosicion}</b>)</> : ""}.<br />
                    Si alguien cancela su reserva en tu horario, te avisaremos al
                    instante por WhatsApp o email. ¡Gracias por tu paciencia!
                  </p>
                ) : (
                  <p>
                    Recibimos tu solicitud <b>#{exito.id}</b>
                    {suc ? <> en <b>{suc.nombre}</b></> : null}.<br />
                    Te confirmamos a la brevedad por teléfono o email.
                  </p>
                )}
                <p style={{ fontSize: 13, color: 'var(--ink-soft)', marginTop: 4 }}>
                  Podés ver el estado de tu reserva en cualquier momento:<br />
                  <a href={`/reserva/${exito.id}`}
                    style={{ color: 'var(--accent)', textDecoration: 'underline', fontWeight: 600 }}>
                    Ver mi reserva
                  </a>
                </p>
              </>
            )}
            {/* WhatsApp para confirmar */}
            {sucConfig?.whatsappNumero && (
              <a
                href={`https://wa.me/${sucConfig.whatsappNumero}?text=Hola! Hice una reserva %23${exito?.id} en ${suc?.nombre ?? ""} y quiero confirmarla.`}
                target="_blank" rel="noreferrer"
                className="rsv-ok-link rsv-ok-wa"
                style={{ marginBottom: "10px" }}
              >
                <MessageCircle size={17} aria-hidden /> Confirmar por WhatsApp
              </a>
            )}

            {/* Redes sociales de ESTA sucursal (antes era un único botón de Instagram fijo para toda la organización) */}
            {sucConfig && (sucConfig.instagramUrl || sucConfig.facebookUrl || sucConfig.websiteUrl || sucConfig.tripadvisorUrl) && (
              <div className="rsv-social">
                {sucConfig.instagramUrl && (
                  <div className="rsv-social-item">
                    <a href={sucConfig.instagramUrl} target="_blank" rel="noreferrer" aria-label="Instagram" className="rsv-social-link">
                      <Instagram size={20} aria-hidden />
                    </a>
                    {handleDeUrl(sucConfig.instagramUrl) && (
                      <span className="rsv-social-handle">{handleDeUrl(sucConfig.instagramUrl)}</span>
                    )}
                  </div>
                )}
                {sucConfig.facebookUrl && (
                  <div className="rsv-social-item">
                    <a href={sucConfig.facebookUrl} target="_blank" rel="noreferrer" aria-label="Facebook" className="rsv-social-link">
                      <Facebook size={20} aria-hidden />
                    </a>
                    {handleDeUrl(sucConfig.facebookUrl) && (
                      <span className="rsv-social-handle">{handleDeUrl(sucConfig.facebookUrl)}</span>
                    )}
                  </div>
                )}
                {sucConfig.tripadvisorUrl && (
                  <div className="rsv-social-item">
                    <a href={sucConfig.tripadvisorUrl} target="_blank" rel="noreferrer" aria-label="TripAdvisor" className="rsv-social-link">
                      <Star size={20} aria-hidden />
                    </a>
                  </div>
                )}
                {sucConfig.websiteUrl && (
                  <div className="rsv-social-item">
                    <a href={sucConfig.websiteUrl} target="_blank" rel="noreferrer" aria-label="Sitio web" className="rsv-social-link">
                      <Globe size={20} aria-hidden />
                    </a>
                  </div>
                )}
              </div>
            )}

            {url ? (
              <>
                <button className="rsv-ok-link" onClick={() => { window.location.href = url; }}>
                  Visitá nuestro Instagram
                </button>
                <p className="rsv-ok-small">Te llevamos automáticamente en {contador}s…</p>
              </>
            ) : (
              <button
                className="rsv-ok-again"
                onClick={() => { setExito(null); setPagoReportado(false); setForm((f) => ({ ...f, nombreCliente: "", telefono: "", email: "", requerimientosEspeciales: "", duracionMinutos: undefined })); }}
              >
                Hacer otra reserva
              </button>
            )}

            {/* Otros establecimientos del grupo (reservables + solo informativos) */}
            {(sucursales.filter((s) => s.id !== suc?.id).length > 0 || (info?.sedesInformativas?.length ?? 0) > 0) && (
              <div className="rsv-otras-sucursales">
                <p className="rsv-otras-sucursales-titulo">Visítanos en nuestros otros establecimientos</p>
                {sucursales.filter((s) => s.id !== suc?.id).length > 0 && (
                  <div className="rsv-otras-sucursales-lista">
                    {sucursales.filter((s) => s.id !== suc?.id).map((s) => (
                      <a key={s.id} href={`/reservar?sucursal=${s.id}`} className="rsv-otra-sucursal">
                        {s.logoUrl
                          ? <img src={s.logoUrl} alt={s.nombre} />
                          : <span className="rsv-otra-sucursal-mono">{s.nombre.charAt(0).toUpperCase()}</span>}
                        <span>{s.nombre}</span>
                      </a>
                    ))}
                  </div>
                )}
                {(info?.sedesInformativas?.length ?? 0) > 0 && (
                  <div className="rsv-sedes-info">
                    {info!.sedesInformativas!.map((s) => (
                      <a key={s.id} href={`/menu/sucursal/${s.id}`} className="rsv-sede-info">
                        {s.logoUrl
                          ? <img src={s.logoUrl} alt={s.nombre} />
                          : <span className="rsv-otra-sucursal-mono">{s.nombre.charAt(0).toUpperCase()}</span>}
                        <span className="rsv-sede-info-txt">
                          <b>{s.nombre}</b>
                          {s.direccion && <small>{s.direccion}</small>}
                          <em>Ver menú</em>
                        </span>
                      </a>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ───────── Formulario ─────────
  return (
    <div className="rsv-root" style={{
      ["--accent" as any]: accent,
      ...(menuConfig?.imagenFondoUrl ? {
        backgroundImage: `linear-gradient(rgba(246,241,231,0.88), rgba(246,241,231,0.88)), url(${menuConfig.imagenFondoUrl})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundAttachment: "fixed",
      } : {}),
    }}>
      <style>{STYLES}</style>
      <div className="rsv-grain" />
      <div className="rsv-wrap">

        <header className="rsv-head rsv-reveal">
          {/* Logo del negocio */}
          {menuConfig?.logoUrl && (
            <img
              src={menuConfig.logoUrl}
              alt={info?.nombre ?? "Logo"}
              style={{ height: 72, width: 72, borderRadius: "50%", objectFit: "cover", margin: "0 auto 14px", display: "block", boxShadow: "0 4px 18px rgba(0,0,0,0.12)", border: `3px solid ${accent}22` }}
            />
          )}
          <p className="rsv-kicker">{info?.nombre ?? "Restaurante"}</p>
          <h1 className="rsv-title">Reservá <em>tu mesa</em></h1>
          <div className="rsv-rule" />
        </header>

        {/* Promoción de eventos públicos activos */}
        {eventosDestacados.length > 0 && (
          <div className="rsv-eventos-banner rsv-reveal">
            {eventosDestacados.map((ev) => (
              <a key={ev.id} href={`/eventos/${ev.id}`} className="rsv-evento-chip">
                <Ticket size={15} />
                <span>{ev.titulo}</span>
                <ArrowRight size={13} />
              </a>
            ))}
          </div>
        )}

        {/* Selector de sucursal — se oculta si solo hay una */}
        {sucursales.length > 1 && (
          <p className="rsv-step-label rsv-reveal" style={{ animationDelay: ".05s" }}>Elegí tu restaurante</p>
        )}
        {sucursales.length > 1 && (
        <div className="rsv-cards">
          {sucursales.map((s, i) => {
            const sel = form.sucursalId === s.id;
            return (
              <button
                key={s.id}
                type="button"
                onClick={() => elegir(s.id)}
                className={`rsv-card rsv-reveal${sel ? " is-sel" : ""}`}
                style={{ ["--c-acc" as any]: acentoDe(s), animationDelay: `${0.1 + i * 0.07}s` }}
              >
                {s.logoUrl
                  ? <img src={s.logoUrl} alt={s.nombre} className="rsv-card-logo" />
                  : <div className="rsv-mono">{s.nombre.charAt(0).toUpperCase()}</div>}
                <h3 className="rsv-card-name">{s.nombre}</h3>
              </button>
            );
          })}
        </div>
        )}

        {/* Formulario (aparece al elegir restaurante, o inmediatamente si hay una sola) */}
        {form.sucursalId && (
          <div id="rsv-form" className="rsv-form rsv-reveal" key={form.sucursalId}>
            <div className="rsv-form-head">
              <h2>{suc?.nombre}</h2>
            </div>

            {error && <div className="rsv-error">{error}</div>}

            <div className="rsv-field rsv-row">
              <div>
                <label className="rsv-label">Documento de identidad</label>
                <input className="rsv-input" value={form.cif}
                  onChange={(e) => set("cif", e.target.value.toUpperCase())}
                  placeholder="V12345678 / J123456789" maxLength={20} />
              </div>
              <div>
                <label className="rsv-label">Teléfono</label>
                <input className="rsv-input" type="tel" value={form.telefono}
                  onChange={(e) => set("telefono", e.target.value)} placeholder="+58 …" />
              </div>
            </div>

            {clienteAutocompletado && (
              <div style={{
                background: 'linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)',
                border: '1px solid #34d399',
                borderRadius: 10,
                padding: '10px 14px',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                animation: 'rsvUp .4s ease both',
                fontSize: 13,
                color: '#065f46',
              }}>
                <UserCheck size={16} style={{ color: '#059669', flexShrink: 0 }} />
                Datos completados de <strong style={{ marginLeft: 2 }}>{clienteAutocompletado}</strong>
              </div>
            )}

            <div className="rsv-field">
              <label className="rsv-label">Nombre completo *</label>
              <input className="rsv-input" value={form.nombreCliente}
                onChange={(e) => set("nombreCliente", e.target.value)} placeholder="Tu nombre" />
            </div>

            <div className="rsv-field rsv-row">
              <div>
                <label className="rsv-label">Email</label>
                <input className="rsv-input" type="email" value={form.email}
                  onChange={(e) => set("email", e.target.value)} placeholder="opcional" />
              </div>
              <div>
                <label className="rsv-label">Fecha de nacimiento</label>
                <input className="rsv-input" type="date" value={form.fechaNacimiento}
                  max={hoyISO()}
                  onChange={(e) => set("fechaNacimiento", e.target.value)} />
              </div>
            </div>

            <fieldset className="rsv-field rsv-fieldset">
              <legend className="rsv-label">
                Sexo
                {sexoSugerido && !form.sexo && (
                  <span style={{ marginLeft: 8, fontSize: 12, color: "#9b8e72" }}>
                    sugerencia por nombre: {sexoSugerido === "F" ? "Femenino" : "Masculino"}
                  </span>
                )}
              </legend>
              <div className="rsv-radios" role="radiogroup" aria-label="Sexo">
                {(["M", "F"] as const).map((s) => {
                  const activo = form.sexo === s;
                  const sugerido = !form.sexo && sexoSugerido === s;
                  return (
                    <label key={s}
                      className={`rsv-radio${activo ? " is-sel" : ""}${sugerido ? " is-sug" : ""}`}>
                      <input type="radio" name="sexo"
                        value={s}
                        checked={activo}
                        onClick={() => { if (form.sexo === s) set("sexo", ""); }}
                        onChange={() => set("sexo", s)}
                      />
                      <span className="rsv-radio-txt">
                        {s === "M" ? "Masculino" : "Femenino"}
                        {sugerido ? " (sugerido)" : ""}
                      </span>
                    </label>
                  );
                })}
                {sexoSugerido && !form.sexo && (
                  <button type="button" onClick={() => set("sexo", sexoSugerido)}
                    className="rsv-radio-apply">
                    Aplicar
                  </button>
                )}
              </div>
            </fieldset>

            <div className="rsv-field rsv-row">
              <div>
                <label className="rsv-label">Fecha *</label>
                <input className="rsv-input" type="date" min={hoyISO()} value={form.fechaReserva}
                  onChange={(e) => {
                    const newFecha = e.target.value;
                    set("fechaReserva", newFecha);
                    // Si la zona tiene horario semanal, ajustar hora al primer slot válido
                    if (zonaSelObj?.horarioSemanal) {
                      const nuevosSlots = generarSlotsParaDia(newFecha, zonaSelObj);
                      if (nuevosSlots.length > 0 && !nuevosSlots.includes(form.horaReserva)) {
                        set("horaReserva", nuevosSlots[0]);
                      }
                    }
                  }} />
              </div>
              <div>
                <label className="rsv-label">Hora *</label>
                {(() => {
                  // Slots según horario semanal de la zona seleccionada
                  const slotsHoy = zonaSelObj
                    ? generarSlotsParaDia(form.fechaReserva, zonaSelObj)
                    : generarSlots();
                  const slots = slotsHoy.length > 0 ? slotsHoy : generarSlots();
                  return (
                    <select className="rsv-select" value={form.horaReserva}
                      onChange={(e) => set("horaReserva", e.target.value)}>
                      {slots.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  );
                })()}
              </div>
            </div>

            {/* Aviso de horario — solo si la zona seleccionada tiene horario semanal */}
            {zonaSelObj?.horarioSemanal && (() => {
              const dia = new Date(form.fechaReserva + 'T00:00:00').getDay();
              const cfg = zonaSelObj.horarioSemanal[String(dia)];
              const DIA_NOM = ['domingo','lunes','martes','miércoles','jueves','viernes','sábado'];
              if (cfg === null || cfg === undefined) {
                return (
                  <div className="rsv-error" style={{ marginBottom: 14, display: 'inline-flex', alignItems: 'center', gap: 6, width: '100%' }}>
                    <AlertTriangle size={15} aria-hidden /> {zonaSelObj.nombre} no atiende los {DIA_NOM[dia]}s. Elegí otra fecha.
                  </div>
                );
              }
              if (cfg.inicio && cfg.fin) {
                const cierre = cfg.nextDay ? `${cfg.fin} (+1 día)` : cfg.fin;
                const diaLabel = DIA_NOM[dia].charAt(0).toUpperCase() + DIA_NOM[dia].slice(1);
                return (
                  <div style={{ background:'var(--paper-deep)', borderRadius:10, padding:'8px 14px', marginBottom:14, fontSize:12.5, color:'var(--ink-soft)', display:'inline-flex', alignItems:'center', gap:6, width:'100%' }}>
                    <Clock size={13} aria-hidden /> {diaLabel}: <strong style={{ color: 'var(--accent)' }}>{cfg.inicio} – {cierre}</strong>
                  </div>
                );
              }
              return null;
            })()}

            <div className="rsv-field">
              <label className="rsv-label">Número de personas *</label>
              <div className="rsv-stepper">
                <button type="button" className="rsv-step-btn"
                  onClick={() => set("numPersonas", Math.max(1, form.numPersonas - 1))}>−</button>
                <span className="rsv-step-val">{form.numPersonas}</span>
                <button type="button" className="rsv-step-btn"
                  onClick={() => set("numPersonas", Math.min(30, form.numPersonas + 1))}>+</button>
              </div>
            </div>

            {info && info.zonas.length > 0 && (
              <div className="rsv-field">
                <label className="rsv-label">¿Dónde preferís sentarte?</label>
                <div className="rsv-chips">
                  {info.zonas.map((z) => (
                    <button key={z.id} type="button"
                      className={`rsv-chip${form.zonaId === z.id ? " is-sel" : ""}`}
                      onClick={() => { set("zonaId", z.id); set("recursoId", undefined); }}>
                      {z.nombre}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Info de pago cuando la zona requiere pago */}
            {zonaSelObj?.requierePagoReserva && (
              <div style={{
                background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
                border: '1px solid #f59e0b',
                borderRadius: 12,
                padding: '14px 16px',
                marginBottom: 8,
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                  <CreditCard size={16} style={{ color: '#92400e' }} />
                  <span style={{ fontWeight: 700, fontSize: 14, color: '#92400e' }}>
                    Pago requerido: ${Number(zonaSelObj.montoReservaUsd ?? 0).toFixed(2)} USD
                  </span>
                </div>
                {zonaSelObj.mensajePoliticaReserva && (
                  <p style={{ fontSize: 13, color: '#78350f', margin: 0, lineHeight: 1.5, whiteSpace: 'pre-line' }}>
                    {zonaSelObj.mensajePoliticaReserva}
                  </p>
                )}
              </div>
            )}

            {/* Selector de mesa/pista cuando la zona lo permite */}
            {zonaSelObj?.permiteSeleccionMesa && (zonaSelObj.recursos ?? []).length > 0 && (
              <div className="rsv-field">
                <label className="rsv-label">
                  Elegir {zonaSelObj.nombre.toLowerCase().includes("bowl") ? "pista" : "mesa"} específica
                  <span style={{ fontWeight: 400, marginLeft: 6, textTransform: "none" }}>(opcional)</span>
                </label>
                <div className="rsv-chips">
                  {(zonaSelObj.recursos ?? []).map((r) => {
                    // Validar si el recurso está disponible según hora y duración
                    const disponible = (() => {
                      if (!r.horaInicio && !r.horaFin) return true;
                      if (!form.horaReserva) return true;
                      const [hh, mm] = form.horaReserva.split(':').map(Number);
                      const minReserva = hh * 60 + mm;
                      const minFin = minReserva + (form.duracionMinutos ?? 0);
                      if (r.horaInicio) {
                        const [ha, ma] = r.horaInicio.split(':').map(Number);
                        if (minReserva < ha * 60 + ma) return false;
                      }
                      if (r.horaFin) {
                        const [hc, mc] = r.horaFin.split(':').map(Number);
                        if (minFin > hc * 60 + mc) return false;
                      }
                      return true;
                    })();
                    return (
                      <button key={r.id} type="button"
                        disabled={!disponible}
                        aria-disabled={!disponible}
                        title={!disponible ? "No disponible en el horario seleccionado" : r.nombre}
                        className={`rsv-chip${form.recursoId === r.id ? " is-sel" : ""}${!disponible ? " rsv-chip-disabled" : ""}`}
                        style={!disponible ? { opacity: 0.4, cursor: "not-allowed" } : {}}
                        onClick={() => disponible && set("recursoId", r.id)}>
                        {r.nombre}
                      </button>
                    );
                  })}
                </div>
                {form.recursoId && (
                  <button type="button" onClick={() => set("recursoId", undefined)}
                    style={{ fontSize: 12, marginTop: 6, color: '#888', background: 'none', border: 'none', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                    <XIcon size={12} aria-hidden /> Sin preferencia de {zonaSelObj.nombre.toLowerCase().includes("bowl") ? "pista" : "mesa"}
                  </button>
                )}
              </div>
            )}

            {/* Duración (solo si la sucursal la usa — canchas, bowling, etc.) */}
            {sucConfig?.usaDuracionReserva && (
              <div className="rsv-field">
                <label className="rsv-label">Duración de la reserva</label>
                <div className="rsv-chips">
                  {(sucConfig.duracionOpciones ?? [60, 90, 120]).map(min => (
                    <button key={min} type="button"
                      className={`rsv-chip${form.duracionMinutos === min ? " is-sel" : ""}`}
                      onClick={() => set("duracionMinutos", min)}>
                      {min >= 60 ? `${Math.floor(min/60)}h${min%60 ? ` ${min%60}min` : ""}` : `${min}min`}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="rsv-field">
              <label className="rsv-label">Requerimientos especiales</label>
              <textarea className="rsv-textarea" value={form.requerimientosEspeciales}
                onChange={(e) => set("requerimientosEspeciales", e.target.value)}
                placeholder="Alergias, silla para bebé, celebración, etc. (opcional)" />
            </div>

            <div className="rsv-field">
              {!mostrarRedes ? (
                <button type="button" onClick={() => setMostrarRedes(true)}
                  style={{ background: "none", border: "none", padding: 0, fontSize: "13px", fontWeight: 600, color: "var(--accent)", cursor: "pointer" }}>
                  + Agregar mis redes sociales (opcional)
                </button>
              ) : (
                <>
                  <label className="rsv-label">Redes sociales (opcional)</label>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px" }}>
                    <input className="rsv-input" placeholder="Instagram @usuario" value={form.instagram} onChange={(e) => set("instagram", e.target.value)} />
                    <input className="rsv-input" placeholder="X @usuario" value={form.twitter} onChange={(e) => set("twitter", e.target.value)} />
                    <input className="rsv-input" placeholder="Facebook" value={form.facebook} onChange={(e) => set("facebook", e.target.value)} />
                    <input className="rsv-input" placeholder="TikTok @usuario" value={form.tiktok} onChange={(e) => set("tiktok", e.target.value)} />
                  </div>
                </>
              )}
            </div>

            {/* Info de confirmación */}
            {(sucConfig?.telefono || sucConfig?.whatsappNumero) && (
              <div style={{ background: "var(--paper-deep)", borderRadius: "12px", padding: "14px 16px", marginBottom: "16px", fontSize: "13px", color: "var(--ink-soft)" }}>
                <strong style={{ color: "var(--ink)" }}>¿Preguntas? Contáctanos para confirmar:</strong>
                <div style={{ marginTop: "6px", display: "flex", gap: "12px", flexWrap: "wrap" }}>
                  {sucConfig.telefono && (
                    <a href={`tel:${sucConfig.telefono}`} style={{ color: "var(--accent)", textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 5 }}>
                      <Phone size={14} aria-hidden /> {sucConfig.telefono}
                    </a>
                  )}
                  {sucConfig.whatsappNumero && (
                    <a href={`https://wa.me/${sucConfig.whatsappNumero}?text=Hola! Quiero confirmar mi reserva en ${suc?.nombre ?? ""}`}
                      target="_blank" rel="noreferrer"
                      style={{ color: "#25D366", textDecoration: "none", fontWeight: 600, display: "inline-flex", alignItems: "center", gap: 5 }}>
                      <MessageCircle size={14} aria-hidden /> WhatsApp
                    </a>
                  )}
                </div>
              </div>
            )}

            <button className="rsv-submit" onClick={enviar} disabled={enviando}>
              {enviando ? "Enviando…" : `Confirmar reserva en ${suc?.nombre}`}
            </button>
          </div>
        )}

        {/* Redes sociales */}
        {sucConfig && (sucConfig.instagramUrl || sucConfig.facebookUrl || sucConfig.websiteUrl || sucConfig.tripadvisorUrl) && (
          <div className="rsv-social">
            {sucConfig.instagramUrl && (
              <a href={sucConfig.instagramUrl} target="_blank" rel="noreferrer" aria-label="Instagram" className="rsv-social-link">
                <Instagram size={18} aria-hidden />
              </a>
            )}
            {sucConfig.facebookUrl && (
              <a href={sucConfig.facebookUrl} target="_blank" rel="noreferrer" aria-label="Facebook" className="rsv-social-link">
                <Facebook size={18} aria-hidden />
              </a>
            )}
            {sucConfig.tripadvisorUrl && (
              <a href={sucConfig.tripadvisorUrl} target="_blank" rel="noreferrer" aria-label="TripAdvisor" className="rsv-social-link">
                <Star size={18} aria-hidden />
              </a>
            )}
            {sucConfig.websiteUrl && (
              <a href={sucConfig.websiteUrl} target="_blank" rel="noreferrer" aria-label="Sitio web" className="rsv-social-link">
                <Globe size={18} aria-hidden />
              </a>
            )}
            {sucConfig.whatsappNumero && (
              <a href={`https://wa.me/${sucConfig.whatsappNumero}`} target="_blank" rel="noreferrer" aria-label="WhatsApp" className="rsv-social-link">
                <MessageCircle size={18} aria-hidden />
              </a>
            )}
          </div>
        )}

        <p className="rsv-foot">
          {info?.nombre ?? ""}
          {suc ? ` · ${suc.nombre}` : ""}
          {form.sucursalId && (
            <>
              {" · "}
              <a
                href={`/menu/sucursal/${form.sucursalId}`}
                target="_blank"
                rel="noreferrer"
                style={{ color: "var(--accent)", textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 4 }}
              >
                Ver menú <UtensilsCrossed size={13} aria-hidden />
              </a>
            </>
          )}
        </p>
        {/* Acceso a "Mis reservas" para reportar pago de reservas anteriores */}
        <p className="rsv-foot" style={{ marginTop: 8 }}>
          ¿Ya tenés una reserva?{" "}
          <a href="/mis-reservas"
            style={{ color: "var(--accent)", textDecoration: "none", fontWeight: 600 }}>
            Buscala con tu cédula, email o teléfono
          </a>
        </p>
      </div>

      {/* ── POPUP de error de disponibilidad ── */}
      {errorModal && (
        <div className="rsv-modal-overlay" onClick={() => setErrorModal(null)}>
          <div className="rsv-modal" onClick={e => e.stopPropagation()}>
            <div className="rsv-modal-head">
              <div className="rsv-modal-icon" aria-hidden>
                <Ban size={28} strokeWidth={2.2} />
              </div>
              <h3>{errorModal.titulo}</h3>
            </div>
            <div className="rsv-modal-body">
              <p>{errorModal.mensaje}</p>
            </div>
            <div className="rsv-modal-actions">
              {errorModal.esBloqueoPista ? (
                <>
                  <button className="rsv-modal-btn-primary"
                    onClick={() => { setErrorModal(null); set("recursoId", undefined); }}>
                    Elegir otra pista
                  </button>
                  <button className="rsv-modal-btn-secondary"
                    onClick={() => { setErrorModal(null); }}>
                    Cambiar horario
                  </button>
                </>
              ) : (
                <button className="rsv-modal-btn-primary"
                  onClick={() => setErrorModal(null)}>
                  Entendido
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
